// Zeppelin theme behaviour: one-tap add to cart, size guide dialogs, the landing-page buy box,
// "pick your size first" and the per-colour concept note on product pages. Loaded as a module next to
// Horizon's own scripts; talks to them only through their public events.
import { CartAddEvent, ThemeEvents } from '@theme/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

// Previewing an unpublished theme: Shopify's preview bar covers the floating add-to-cart (zeppelin.css lifts it).
if (window.Shopify?.theme?.role === 'unpublished' && !window.Shopify.designMode) {
  document.documentElement.classList.add('zep-theme-preview');
}

async function addVariant(variantId, button) {
  const label = button.innerHTML;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error('add failed');
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    document.dispatchEvent(
      new CartAddEvent(cart, 'zep-add', { source: 'zep-add', itemCount: cart.item_count, variantId: String(variantId) })
    );
    button.textContent = button.dataset.addedLabel || 'Added';
    setTimeout(() => {
      if (!button.isConnected) return;
      button.innerHTML = label;
      button.disabled = false;
    }, 1800);
  } catch (error) {
    button.textContent = 'Try again';
    button.disabled = false;
  } finally {
    button.removeAttribute('aria-busy');
  }
}

// ----- pick your size first ------------------------------------------------
const pending = new Set();

function initRequireChoice() {
  if (new URLSearchParams(location.search).has('variant')) return;
  document.querySelectorAll('[data-zep-require-choice]').forEach((holder) => {
    const section = holder.closest('.shopify-section') || document;
    const picker = section.querySelector('variant-picker');
    if (!picker) return;
    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.dataset.currentChecked = 'false';
    });
    let hint = section.querySelector('.zep-choice-hint');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'zep-choice-hint';
      hint.setAttribute('role', 'status');
      picker.after(hint);
    }
    hint.textContent = holder.dataset.zepRequireChoice || 'Select a size';
    // The sticky bar names the first size until one is chosen; label it as a prompt instead.
    section.querySelector('.sticky-add-to-cart__variant')?.setAttribute('data-zep-prompt', 'Select a size');
    section.classList.add('zep-needs-choice');
    pending.add(section);
    picker.addEventListener(
      'change',
      () => {
        pending.delete(section);
        section.classList.remove('zep-needs-choice', 'zep-nudge');
      },
      { once: true }
    );
  });
}

function nudge(section) {
  const picker = section.querySelector('variant-picker');
  section.classList.remove('zep-nudge');
  void section.offsetWidth;
  section.classList.add('zep-nudge');
  picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  picker?.querySelector('input[type="radio"]:not([disabled]) + label, input[type="radio"]:not([disabled])')?.focus?.({ preventScroll: true });
}

document.addEventListener(
  'click',
  (event) => {
    if (!pending.size) return;
    const atc = event.target.closest('[ref="addToCartButton"], .shopify-payment-button button');
    if (!atc) return;
    const inSticky = atc.closest('sticky-add-to-cart');
    const section = inSticky ? [...pending][0] : [...pending].find((s) => s.contains(atc));
    if (!section) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    nudge(section);
  },
  true
);

// ----- concept note per colour -----------------------------------------------
// Horizon re-renders the section for the new variant but only swaps its own parts; copy the note's state from that render.
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const fresh = event.detail?.data?.html?.querySelector('[data-zep-concept]');
  if (!fresh) return;
  document.querySelectorAll('[data-zep-concept]').forEach((note) => {
    note.hidden = fresh.hidden;
  });
});

// ----- landing buy box ---------------------------------------------------------
// A size pick arms the add button with that variant; the one-tap add below does the rest.
document.addEventListener('click', (event) => {
  const size = event.target.closest('[data-zep-lp-sizes] [role="radio"]');
  const box = event.target.closest('[data-zep-lp-buy]');
  if (!box) return;
  if (size && !size.disabled) {
    box.querySelectorAll('[data-zep-lp-sizes] [role="radio"]').forEach((b) => b.setAttribute('aria-checked', String(b === size)));
    box.querySelector('[data-zep-lp-add]').dataset.zepAdd = size.dataset.variantId;
    box.classList.remove('is-missing');
    return;
  }
  if (event.target.closest('[data-zep-lp-add]:not([data-zep-add])')) {
    box.classList.add('is-missing');
    box.querySelector('[data-zep-lp-sizes] [role="radio"]:not([disabled])')?.focus();
  }
});

// ----- landing floating add to cart ---------------------------------------------------
// Shown once the hero's buy box has scrolled away, hidden while the closing buy box is on screen. Its size picker and
// the hero's size buttons stay in step, so a size chosen in either place is the one added.
const lpFloat = document.querySelector('[data-zep-lp-float]');
const lpHero = document.querySelector('[data-zep-lp-hero]');
if (lpFloat && lpHero) {
  const select = lpFloat.querySelector('[data-zep-lp-float-size]');
  const add = lpFloat.querySelector('[data-zep-lp-float-add]');
  const seen = { hero: true, end: false };
  const sync = () => {
    const show = !seen.hero && !seen.end;
    lpFloat.classList.toggle('is-shown', show);
    lpFloat.setAttribute('aria-hidden', String(!show));
    select.tabIndex = add.tabIndex = show ? 0 : -1;
  };
  new IntersectionObserver(([e]) => { seen.hero = e.isIntersecting; sync(); }).observe(lpHero);
  const end = document.querySelector('[data-zep-lp-buy]:not([data-zep-lp-hero])');
  if (end) new IntersectionObserver(([e]) => { seen.end = e.isIntersecting; sync(); }, { rootMargin: '0px 0px -15% 0px' }).observe(end);

  const pick = (variantId) => {
    select.value = variantId;
    if (variantId) add.dataset.zepAdd = variantId; else delete add.dataset.zepAdd;
    lpFloat.classList.remove('is-missing');
    document.querySelectorAll('[data-zep-lp-buy]').forEach((box) => {
      box.querySelectorAll('[data-zep-lp-sizes] [role="radio"]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.variantId === variantId)));
      const boxAdd = box.querySelector('[data-zep-lp-add]');
      if (variantId) boxAdd.dataset.zepAdd = variantId; else delete boxAdd.dataset.zepAdd;
      box.classList.remove('is-missing');
    });
  };
  select.addEventListener('change', () => pick(select.value));
  document.addEventListener('click', (event) => {
    const size = event.target.closest('[data-zep-lp-sizes] [role="radio"]');
    if (size && !size.disabled) pick(size.dataset.variantId);
    if (event.target.closest('[data-zep-lp-float-add]:not([data-zep-add])')) {
      lpFloat.classList.add('is-missing');
      select.focus();
      try { select.showPicker(); } catch {}
    }
  });
}

// ----- landing hero slideshow --------------------------------------------------------
// Crossfades on a timer. Holds still under reduced motion, while a mouse is over it, and while it is off screen;
// a swipe or a dot moves it by hand.
document.querySelectorAll('[data-zep-lp-slides]').forEach((box) => {
  const slides = [...box.querySelectorAll('.zep-lp-buy__slide')];
  const dots = [...box.querySelectorAll('[data-zep-lp-dot]')];
  const wait = (Number(box.dataset.seconds) || 5) * 1000;
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let at = 0, timer = 0, held = false, seen = true, startX = null;
  const show = (n) => {
    at = (n + slides.length) % slides.length;
    slides.forEach((s, i) => s.classList.toggle('is-active', i === at));
    dots.forEach((d, i) => d.setAttribute('aria-current', String(i === at)));
  };
  const run = () => {
    clearTimeout(timer);
    if (!still && !held && seen && !document.hidden) timer = setTimeout(() => { show(at + 1); run(); }, wait);
  };
  dots.forEach((d, i) => d.addEventListener('click', () => { show(i); run(); }));
  box.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { held = true; run(); } });
  box.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { held = false; run(); } });
  box.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
  box.addEventListener('touchend', (e) => {
    const dx = startX === null ? 0 : e.changedTouches[0].clientX - startX;
    startX = null;
    if (Math.abs(dx) > 40) { show(at + (dx < 0 ? 1 : -1)); run(); }
  });
  new IntersectionObserver(([e]) => { seen = e.isIntersecting; run(); }).observe(box);
  document.addEventListener('visibilitychange', run);
});

// ----- comparison tables on phones ---------------------------------------------------
// Each answer carries its column's name, so the stacked phone layout can label it (zep-lp.css).
document.querySelectorAll('.comparison-table__table').forEach((table) => {
  const heads = [...table.querySelectorAll('thead th')].map((th) => th.textContent.trim());
  table.querySelectorAll('tbody tr').forEach((row) => {
    [...row.children].forEach((cell, i) => {
      if (cell.tagName === 'TD' && heads[i]) cell.dataset.label = heads[i];
    });
  });
});

// ----- clicks --------------------------------------------------------------
document.addEventListener('click', (event) => {
  const add = event.target.closest('[data-zep-add]');
  if (add) {
    event.preventDefault();
    addVariant(add.dataset.zepAdd, add);
    return;
  }
  const open = event.target.closest('[data-zep-dialog-open]');
  if (open) {
    document.getElementById(open.dataset.zepDialogOpen)?.showModal();
    return;
  }
  const close = event.target.closest('[data-zep-dialog-close]');
  if (close) {
    close.closest('dialog')?.close();
    return;
  }
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('zep-dialog')) {
    event.target.close();
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initRequireChoice);
else initRequireChoice();
